import React, { useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { ColDef } from "ag-grid-community";
import { MapPin } from "lucide-react";

import PaginationLayout from "../components/PaginationLayout";
import { GlobalContext } from "../context/Provider";
import henceforthApi from "../utils/henceforthApi";
import henceofrthEnums from "../utils/henceofrthEnums";
import AgGridTable from "../components/ui/AgGridTable";

interface Ride {
  data: {
    data: Array<any>;
    total_count: number;
  };
}

function RideCreated({ _id }: any) {
  const [page, setPage] = useState<number>(1);
  const [state, setState] = useState<Ride>({
    data: {
      data: [],
      total_count: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const limit = 10;
  const { authState } = useContext(GlobalContext);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const onChangePagination = (newVal: number) => {
    setPage(Number(newVal));
  };

  const getRideDetails = async () => {
    setLoading(true);
    try {
      const rideDetail = await henceforthApi.Riding.pagination(
        _id,
        page - 1,
        limit
      );
      setState(rideDetail);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRideDetails();
  }, [page]);

  const formatCurrency = (amount: number, currency: any) => {
    const formatted = Number(amount || 0).toFixed(2);
    if (currency === henceofrthEnums.CurrencyType.NGN) return `₦ ${formatted}`;
    if (currency === henceofrthEnums.CurrencyType.INR) return `₹ ${formatted}`;
    return `$ ${formatted}`;
  };

  // AG Grid Column Definitions
  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "Sr No.",
        minWidth: 70,
        maxWidth: 80,
        width: 75,
        valueGetter: (p) => {
          return (page - 1) * limit + (p.node?.rowIndex ?? 0) + 1;
        },
        cellClass: "font-medium text-slate-500",
      },
      {
        headerName: "Pick up Location",
        field: "address_from",
        minWidth: 180,
        flex: 1,
        cellRenderer: (p: any) => {
          const val = p.value || "Not available";
          return (
            <div className="flex items-center gap-1.5 text-slate-700 truncate text-sm" title={val}>
              <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="truncate">{val}</span>
            </div>
          );
        },
      },
      {
        headerName: "Drop off Location",
        field: "address_to",
        minWidth: 180,
        flex: 1,
        cellRenderer: (p: any) => {
          const val = p.value || "Not available";
          return (
            <div className="flex items-center gap-1.5 text-slate-700 truncate text-sm" title={val}>
              <MapPin className="w-4 h-4 text-rose-500 shrink-0" />
              <span className="truncate">{val}</span>
            </div>
          );
        },
      },
      {
        headerName: "Price",
        field: "price",
        minWidth: 100,
        width: 110,
        cellRenderer: (p: any) => {
          return (
            <span className="font-bold text-slate-900 text-sm">
              {p.value ? formatCurrency(p.value, p.data?.currency) : "Not available"}
            </span>
          );
        },
      },
      {
        headerName: "Date",
        field: "created_at",
        minWidth: 120,
        width: 130,
        valueFormatter: (p) => {
          return p.value ? moment(Number(p.value)).format("DD MMM YYYY") : "N/A";
        },
      },
    ];
  }, [page]);

  return (
    <>
      <AgGridTable
        rowData={state?.data?.data || []}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No created rides found"
      />

      <div className="mt-3">
        <PaginationLayout
          count={state?.data?.total_count || 0}
          data={state?.data?.data || []}
          page={page}
          limit={Number(limit)}
          loading={loading}
          onPageChange={(val) => onChangePagination(val)}
        />
      </div>
    </>
  );
}

export default RideCreated;
